import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import './learn.css';

// =========================================================================
// /learn/<slug> — Editorial-engine article page.
// Data source: /learn/articles.json (25 complete articles delivered in
// Fundle_SEO_AEO_Engine_Kit.zip, each with article_html + metadata).
// Diagram: /learn/diagrams/<slug>.svg
//
// Rendering strategy:
//   * The delivered article_html is a self-contained semantic body fragment
//     (h1 + sections + answer boxes + FAQs). We render it inside the shared
//     Fundle chrome (NavigationZoho + FooterZoho via App.js SiteChrome) with a
//     self-contained long-form reading layout from ./learn.css — no mirror CSS.
//   * We inject Article + BreadcrumbList + FAQPage JSON-LD per prompt 2's
//     "generate schema from visible content" rule — using the real author,
//     dates and canonical URL from the article record.
// =========================================================================

const setMeta = (name, value) => {
  if (!value) return;
  let el = document.head.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
};
const setLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};
const setJsonLd = (id, obj) => {
  document.head
    .querySelectorAll(`script[data-mirror-jsonld="${id}"]`)
    .forEach((s) => s.remove());
  const s = document.createElement('script');
  s.type = 'application/ld+json';
  s.text = JSON.stringify(obj);
  s.setAttribute('data-mirror-jsonld', id);
  document.head.appendChild(s);
};

const PILLAR_LABELS = {
  loyalty: 'Loyalty',
  campaigns: 'Campaigns',
  marketplace: 'Marketplace intelligence',
  mbo: 'MBO',
  sis: 'SIS',
  ebo: 'EBO',
  'social-commerce': 'Social commerce',
  malls: 'Malls',
  'retail-ai': 'Governed retail AI',
};

const CANONICAL_HOST = 'https://fundle.ai';

const LearnArticle = () => {
  const { slug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [articles, setArticles] = useState(null);
  const [cmsList, setCmsList] = useState([]); // published CMS articles for related links
  const [apiArticle, setApiArticle] = useState(null); // scheduler/CMS-authored article fallback
  const [apiProbed, setApiProbed] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let aborted = false;
    fetch('/learn/articles.json')
      .then((r) => r.json())
      .then((j) => {
        if (!aborted) setArticles(j.articles || []);
      })
      .catch((e) => !aborted && setError(e));
    return () => { aborted = true; };
  }, []);

  const article = useMemo(
    () => (articles || []).find((a) => a.slug === slug) || apiArticle,
    [articles, apiArticle, slug],
  );

  // Pull the published CMS list once so we can build same-pillar "keep reading"
  // links across both the shipped seed and every live content-engine article.
  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${base}/api/content/v1/public/articles`)
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((j) => setCmsList(j.items || []))
      .catch(() => {});
  }, []);

  const related = useMemo(() => {
    if (!article) return [];
    const pool = [...(articles || []), ...cmsList];
    const seen = new Set([article.slug]);
    const out = [];
    for (const a of pool) {
      if (!a || !a.slug || seen.has(a.slug)) continue;
      if (a.pillar !== article.pillar) continue;
      seen.add(a.slug);
      out.push(a);
      if (out.length === 3) break;
    }
    return out;
  }, [article, articles, cmsList]);

  // Fallback: if the slug is not in the static seed JSON, probe the content-engine
  // public endpoint for a scheduler- or CMS-authored article.
  // We wait for the seed JSON to finish loading (articles !== null) before probing
  // so there is exactly ONE fetch and no abort/re-run race that could strand the
  // payload and force a spurious redirect to /learn.
  useEffect(() => {
    if (!slug) return;
    if (articles === null) return;            // seed not loaded yet — wait
    if (apiProbed) return;                     // already resolved once
    if (articles.some((a) => a.slug === slug)) { setApiProbed(true); return; }
    let aborted = false;
    const base = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${base}/api/content/v1/public/articles/${encodeURIComponent(slug)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((payload) => {
        if (aborted) return;                   // never commit state after unmount/re-run
        if (payload) {
          // Shape it exactly like a seed article so the existing render path works.
          setApiArticle({
            slug: payload.slug,
            title: payload.title,
            seo_title: payload.title,
            meta_description: payload.meta_description,
            pillar: payload.pillar,
            article_html: payload.article_html,
            faq: payload.faq || [],
            sources: payload.sources || [],
            author: payload.author,
            prepared_at: payload.updated_at || new Date().toISOString(),
            _cms_sourced: true,
          });
        }
        setApiProbed(true);
      })
      .catch(() => { if (!aborted) setApiProbed(true); });
    return () => { aborted = true; };
  }, [slug, articles, apiProbed]);

  useEffect(() => {
    if (!article) return;
    const url = `${CANONICAL_HOST}/learn/${article.slug}`;
    document.title = article.seo_title || article.title;
    setMeta('description', article.meta_description);
    setLink('canonical', url);
    window.scrollTo(0, 0);

    const articleLd = {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.meta_description,
      inLanguage: 'en',
      datePublished: article.prepared_at,
      dateModified: article.prepared_at,
      mainEntityOfPage: url,
      author: {
        '@type': 'Organization',
        name: article.author?.name || 'Fundle',
        url: article.author?.url || 'https://fundle.ai/about',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Fundle',
        url: 'https://fundle.ai',
        logo: {
          '@type': 'ImageObject',
          url: 'https://fundle.ai/assets/brand/fundle-logo-white.png',
        },
      },
    };
    const breadcrumbLd = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: CANONICAL_HOST },
        { '@type': 'ListItem', position: 2, name: 'Learn', item: `${CANONICAL_HOST}/learn` },
        { '@type': 'ListItem', position: 3, name: article.title, item: url },
      ],
    };
    const faqLd = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: (article.faq || []).map((q) => ({
        '@type': 'Question',
        name: q.question,
        acceptedAnswer: { '@type': 'Answer', text: q.answer },
      })),
    };
    setJsonLd('learn-article', articleLd);
    setJsonLd('learn-breadcrumb', breadcrumbLd);
    if ((article.faq || []).length) setJsonLd('learn-faq', faqLd);

    return () => {
      ['learn-article', 'learn-breadcrumb', 'learn-faq'].forEach((id) =>
        document.head
          .querySelectorAll(`script[data-mirror-jsonld="${id}"]`)
          .forEach((s) => s.remove()),
      );
    };
  }, [article]);

  // Intercept internal <a> clicks inside the injected HTML so navigation stays SPA.
  useEffect(() => {
    const root = document.getElementById('learn-article-body');
    if (!root) return;
    const onClick = (e) => {
      let a = e.target;
      while (a && a.tagName !== 'A') a = a.parentElement;
      if (!a) return;
      const href = a.getAttribute('href');
      if (!href) return;
      if (/^(https?:)?\/\//i.test(href)) return;
      if (href.startsWith('mailto:') || href.startsWith('tel:') || href.startsWith('#')) return;
      if (a.target === '_blank' || a.hasAttribute('download')) return;
      e.preventDefault();
      navigate(href);
    };
    root.addEventListener('click', onClick);
    return () => root.removeEventListener('click', onClick);
  }, [article, navigate]);

  // Crawler-renderer readiness (repair brief §1): only allow the snapshot to be
  // captured once the article is resolved or definitively settled — never
  // during the loading shell.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (article) window.prerenderReady = true;
    else if (error || (articles !== null && apiProbed)) window.prerenderReady = true;
    else window.prerenderReady = false;
  }, [article, error, articles, apiProbed]);

  if (error) return <div className="site-shell"><p>Could not load article.</p></div>;
  if (articles === null) return <div data-testid="learn-loading" style={{ minHeight: '60vh' }} />;
  // Wait for the API fallback probe before 404-ing.
  if (!article && !apiProbed) return <div data-testid="learn-loading" style={{ minHeight: '60vh' }} />;
  if (!article) return <Navigate to="/learn" replace />;

  const pillarLabel = PILLAR_LABELS[article.pillar] || article.pillar;
  const diagramUrl = `/learn/diagrams/${article.slug}.svg`;

  return (
    <main className="learn-article" data-testid="learn-article" data-slug={article.slug}>
      <article className="learn-container" style={{
        maxWidth: 820,
        margin: '0 auto',
        padding: '48px 24px 96px',
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        color: '#0D1B2A',
      }}>
        <nav aria-label="Breadcrumb" style={{ fontSize: 13, color: '#4b5a74', marginBottom: 24 }}>
          <a href="/" style={{ color: 'inherit' }}>Home</a> ›{' '}
          <a href="/learn" style={{ color: 'inherit' }}>Learn</a> ›{' '}
          <span style={{ color: '#0D1B2A', fontWeight: 600 }}>{pillarLabel}</span>
        </nav>

        <p style={{
          fontSize: 11,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          fontWeight: 700,
          color: '#1E3A8A',
          marginBottom: 16,
        }}>
          Fundle Learn · {pillarLabel}
        </p>

        <div
          id="learn-article-body"
          className="learn-body"
          dangerouslySetInnerHTML={{ __html: article.article_html }}
        />

        <section style={{
          marginTop: 48,
          padding: 24,
          border: '1px solid #E2E8F0',
          borderRadius: 12,
          background: '#F8FAFC',
        }}>
          <p style={{ fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#4b5a74', margin: 0, marginBottom: 12, fontWeight: 700 }}>
            Workflow diagram
          </p>
          <img
            src={diagramUrl}
            alt={`${article.title} — workflow diagram`}
            style={{ width: '100%', height: 'auto', display: 'block' }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </section>

        <section style={{ marginTop: 48 }}>
          <h2 style={{ fontSize: 22, marginBottom: 12 }}>Sources</h2>
          <ul style={{ paddingLeft: 20, lineHeight: 1.75 }}>
            {(article.sources || []).map((s, i) => (
              <li key={i}>
                <a href={s.url} target="_blank" rel="noopener noreferrer nofollow"
                   style={{ color: '#1E3A8A', fontWeight: 600 }}>{s.title} ↗</a>
                {s.supports && <> — <span style={{ color: '#4b5a74' }}>{s.supports}</span></>}
              </li>
            ))}
          </ul>
        </section>

        {related.length > 0 && (
          <section data-testid="learn-related" style={{ marginTop: 48 }}>
            <h2 style={{ fontSize: 22, marginBottom: 16 }}>Keep reading</h2>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
              {related.map((r) => (
                <li key={r.slug}>
                  <a
                    href={`/learn/${r.slug}`}
                    data-testid={`learn-related-card-${r.slug}`}
                    onClick={(e) => { e.preventDefault(); navigate(`/learn/${r.slug}`); }}
                    style={{
                      display: 'block',
                      padding: 18,
                      border: '1px solid #E2E8F0',
                      borderRadius: 12,
                      background: '#F8FBFF',
                      textDecoration: 'none',
                      color: '#0D1B2A',
                      height: '100%',
                    }}
                  >
                    <p style={{ fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', fontWeight: 700, color: '#1E3A8A', margin: 0 }}>
                      {PILLAR_LABELS[r.pillar] || r.pillar}
                    </p>
                    <p style={{ fontSize: 15.5, fontWeight: 700, margin: '8px 0 0', lineHeight: 1.3 }}>
                      {r.title}
                    </p>
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        <aside style={{
          marginTop: 48,
          padding: '24px 28px',
          background: '#0D1B2A',
          color: '#F8FAFC',
          borderRadius: 12,
        }}>
          <p style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, color: '#C6F432', margin: 0, marginBottom: 10 }}>
            See it in Fundle Brain
          </p>
          <p style={{ fontSize: 18, marginBottom: 16, lineHeight: 1.5 }}>
            Try this workflow on your own retail or mall data. A Fundle solutions architect will show you how it reads, decides and prepares the next useful action.
          </p>
          <a href="/book-demo" style={{
            display: 'inline-block',
            padding: '10px 18px',
            background: '#C6F432',
            color: '#0D1B2A',
            borderRadius: 999,
            fontWeight: 700,
            textDecoration: 'none',
            fontSize: 14,
          }}>Book a working session ↗</a>
        </aside>

        <footer style={{ marginTop: 48, fontSize: 13, color: '#4b5a74', borderTop: '1px solid #E2E8F0', paddingTop: 20 }}>
          Published by {article.author?.name || 'Fundle'} · {article.prepared_at} · Pillar: {pillarLabel}
        </footer>
      </article>
    </main>
  );
};

export default LearnArticle;
